/**
 * C5 Excel-import view-model — pure display shaping kept out of the .vue so
 * node:test can cover it directly (the companies repo has no SFC test runner).
 *
 * The REAL §4 backend owns parsing + validation. POST /employees/import (a
 * multipart file upload) returns:
 *   { detectedColumns[], suggestedMapping{}, preview[≤5 rows], total, valid, invalid }
 * where every preview row's `nationalId` is already MASKED ("****last4") and
 * `valid`/`invalid` are the authoritative server-side counts. So this module
 * NEVER re-validates or re-formats — re-validating a masked id would be wrong
 * (it is not raw digits) and re-masking would leak intent. It only shapes the
 * response for the preview table + the column-mapping panel.
 */

function str(v) {
  return v == null ? '' : String(v).trim();
}

// Matches the backend exactly (do not drift from these): EmployeesImportService
// parses with ExcelJS's `wb.xlsx.load()` — .xlsx ONLY, no legacy .xls (OLE2) or
// .csv support — and FileValidationPipe's DEFAULT_MAX_BYTES is 10 MiB
// (backend/src/common/files/file-validation.pipe.ts,
// backend/src/common/files/upload.interceptor.ts's MAX_UPLOAD_BYTES). Rejecting
// client-side before the multipart round-trip avoids a slow failed upload for an
// obviously-wrong file (wrong extension, or too large for the interceptor's
// streaming limit to even accept).
var MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;
var ALLOWED_IMPORT_EXTENSIONS = ['.xlsx'];

function fileExtension(name) {
  var s = str(name).toLowerCase();
  var i = s.lastIndexOf('.');
  return i === -1 ? '' : s.slice(i);
}

/**
 * Validate a File (or File-like { name, size }) before it is uploaded.
 * Returns { valid: true } or { valid: false, error: 'type' | 'size' | 'required' }.
 */
function validateImportFile(file) {
  if (!file) return { valid: false, error: 'required' };
  var ext = fileExtension(file.name);
  if (ALLOWED_IMPORT_EXTENSIONS.indexOf(ext) === -1) {
    return { valid: false, error: 'type' };
  }
  if (typeof file.size === 'number' && file.size > MAX_IMPORT_FILE_BYTES) {
    return { valid: false, error: 'size' };
  }
  return { valid: true };
}

/**
 * Shape the backend preview rows into display rows for the table. `index` is the
 * real spreadsheet row number (the backend emits `row`, 2-based since row 1 is
 * the header) so the user can locate a row in their own file.
 */
function shapePreview(rows) {
  var list = rows || [];
  return list.map(function (r, i) {
    var row = r || {};
    return {
      index: row.row != null ? row.row : i + 1,
      fullName: str(row.fullName),
      nationalId: str(row.nationalId), // already masked by the backend — passed through verbatim
      memberCode: str(row.memberCode), // insurer member id (the dedupe key)
      relation: str(row.relation), // principal | spouse | child | other
      birthDate: str(row.birthDate),
      cardNumber: str(row.cardNumber),
      mobile: str(row.mobile),
      email: str(row.email),
      department: str(row.department),
      policyNumber: str(row.policyNumber),
      tier: str(row.tier),
      errors: rowErrors(row),
    };
  });
}

var AR_DIGITS = '٠١٢٣٤٥٦٧٨٩';

/** Normalize Arabic-Indic digits to ASCII so "٢٩٩..." typed in Excel validates. */
function normalizeDigits(v) {
  return str(v).replace(/[٠-٩]/g, function (d) {
    return String(AR_DIGITS.indexOf(d));
  });
}

/** Egyptian national ID = exactly 14 digits (same rule as the backend isValidRow). */
function isValidNationalId(v) {
  return /^\d{14}$/.test(normalizeDigits(v));
}

/**
 * Per-row error codes for the preview. Authoritative source is the server
 * (row.errors: ['NATIONAL_ID_INVALID', ...] or [{ field, code }] or row.error);
 * when the server sent none we fall back to checks that are SAFE on masked data:
 * missing name, and — only when the id is still raw digits (never the "****1234"
 * mask, which cannot be length-checked) — a non-14-digit id. Codes are mapped to
 * i18n keys by the .vue via errorKey().
 */
function rowErrors(row) {
  var r = row || {};
  // The new backend marks each row valid/invalid explicitly. Trust it and skip
  // the legacy client-side fallback — otherwise a valid, id-less dependent (or a
  // member identified by memberCode/staffCode) is wrongly flagged "national id
  // missing". Only rows WITHOUT server validity info fall through to the fallback.
  if (r.valid === true) return [];
  if (Array.isArray(r.reasons)) return r.reasons.filter(Boolean);
  var out = [];
  function push(code) {
    if (code && out.indexOf(code) === -1) out.push(code);
  }
  // The new backend reports per-row reasons (NO_NAME, NO_IDENTITY, …); older
  // callers used errors/error. Accept all three.
  var server =
    r.reasons != null
      ? r.reasons
      : r.errors != null
      ? r.errors
      : r.error != null
      ? [r.error]
      : [];
  if (!Array.isArray(server)) server = [server];
  server.forEach(function (e) {
    if (e == null) return;
    if (typeof e === 'string') return push(e);
    push(e.code || e.message);
  });
  if (out.length) return out;
  if (!str(r.fullName)) push('NAME_REQUIRED');
  var nid = str(r.nationalId);
  if (!nid) push('NATIONAL_ID_REQUIRED');
  else if (/^[\d٠-٩]+$/.test(nid) && !isValidNationalId(nid)) push('NATIONAL_ID_INVALID');
  return out;
}

var KNOWN_ROW_ERRORS = [
  // analyzer reasons (new backend)
  'NO_NAME',
  'NO_IDENTITY',
  'NAME_REQUIRED',
  'NATIONAL_ID_REQUIRED',
  'NATIONAL_ID_INVALID',
  'NATIONAL_ID_DUPLICATE',
  'DUPLICATE',
  'DEPARTMENT_NOT_FOUND',
  'POLICY_NOT_FOUND',
  'POLICY_NUMBER_UNKNOWN',
  // S10/M12: tier (الفئة) on an import row did not resolve to a contract-tree
  // entity — the employee is still imported, just without a tier (assignmentWarnings).
  'TIER_NOT_FOUND',
  'EMAIL_INVALID',
];

/** i18n sub-key (under employee-import.rowErrors) for a row error code. */
function errorKey(code) {
  var c = String(code || '').toUpperCase();
  return KNOWN_ROW_ERRORS.indexOf(c) !== -1 ? c : 'UNKNOWN';
}

/** Import-level API error -> i18n key suffix (employee-import.apiErrors.*). */
function importErrorKey(err) {
  var data = err && err.response && err.response.data;
  var code = data && data.code;
  var known = ['INVALID_IMPORT_FILE', 'IMPORT_TOO_MANY_ROWS', 'VALIDATION_ERROR', 'FORBIDDEN', 'UNSUPPORTED_MEDIA_TYPE', 'PAYLOAD_TOO_LARGE'];
  if (code && known.indexOf(code) !== -1) return code;
  var status = err && err.response && err.response.status;
  if (status === 413) return 'PAYLOAD_TOO_LARGE';
  if (status === 403) return 'FORBIDDEN';
  return 'generic';
}

/** assignmentWarnings from confirm: employee imported but dept/policy left unassigned. */
function assignmentWarnings(res) {
  return ((res && res.assignmentWarnings) || []).map(function (w) {
    return { row: w && w.row, code: errorKey(w && w.code) };
  });
}

/**
 * Whole-file invalid-row list when the server provides one on the preview
 * response (result.errors / result.invalidRows: [{ row, code|errors[] }]).
 * Returns [{ row, codes[] }]; empty when the server only sent counts.
 */
function invalidRows(result) {
  var list = (result && (result.invalidRows || result.errors)) || [];
  if (!Array.isArray(list)) return [];
  return list.map(function (e) {
    var raw =
      e &&
      (e.reasons != null
        ? e.reasons
        : e.errors != null
        ? e.errors
        : e.code != null
        ? [e.code]
        : []);
    if (!Array.isArray(raw)) raw = [raw];
    var codes = raw
      .map(function (x) {
        return x && typeof x === 'object' ? x.code || x.message : x;
      })
      .filter(Boolean);
    if (!codes.length) codes = ['UNKNOWN'];
    return { row: e && e.row, codes: codes };
  });
}

/** Per-row errors reported by /employees/import/confirm ({ row, code }[]). */
function confirmErrors(res) {
  var list = (res && res.errors) || [];
  return list.map(function (e) {
    return { row: e && e.row, code: errorKey(e && e.code) };
  });
}

/** The authoritative { total, valid, invalid } counts from the import response. */
function summarizeResult(result) {
  var r = result || {};
  function n(v) {
    return typeof v === 'number' && isFinite(v) ? v : 0;
  }
  return { total: n(r.total), valid: n(r.valid), invalid: n(r.invalid) };
}

/**
 * detectedColumns + suggestedMapping → [{ src, dst|null }] for the editable
 * mapping UI. Accepts the new backend shape (detectedColumns: [{header, field}])
 * and the legacy one (detectedColumns: [string]).
 */
function buildMapRows(detectedColumns, suggestedMapping) {
  var cols = detectedColumns || [];
  var map = suggestedMapping || {};
  return cols.map(function (c) {
    var src = c && typeof c === 'object' ? str(c.header) : str(c);
    var fromCol = c && typeof c === 'object' && c.field ? c.field : null;
    var dst =
      fromCol ||
      (Object.prototype.hasOwnProperty.call(map, src) ? map[src] : null) ||
      null;
    return { src: src, dst: dst };
  });
}

// The member fields a column can be mapped to in the editable panel. '' = ignore.
var FIELD_OPTIONS = [
  'fullName', 'nameFirst', 'nameMiddle', 'nameFamily', 'nameLast',
  'nationalId', 'memberCode', 'staffCode', 'cardNumber', 'relation',
  'principalCode', 'gender', 'maritalStatus', 'birthDate', 'coverageStart',
  'coverageEnd', 'mobile', 'email', 'department', 'policyNumber', 'tier',
  'nationality', 'jobTitle',
];

/** Edited mapRows → { header: field } to re-POST as the `mapping` override ('' = ignore). */
function mappingFromRows(mapRows) {
  var out = {};
  (mapRows || []).forEach(function (m) {
    if (m && m.src) out[m.src] = m.dst || '';
  });
  return out;
}

/** One-time login credentials returned by /import/confirm ({ row, email, temporaryPassword }[]). */
function credentials(res) {
  return ((res && res.credentials) || []).map(function (c) {
    return {
      row: c && c.row,
      email: str(c && c.email),
      password: str(c && c.temporaryPassword),
    };
  });
}

module.exports = {
  str: str,
  shapePreview: shapePreview,
  summarizeResult: summarizeResult,
  buildMapRows: buildMapRows,
  validateImportFile: validateImportFile,
  isValidNationalId: isValidNationalId,
  normalizeDigits: normalizeDigits,
  rowErrors: rowErrors,
  errorKey: errorKey,
  importErrorKey: importErrorKey,
  confirmErrors: confirmErrors,
  invalidRows: invalidRows,
  assignmentWarnings: assignmentWarnings,
  mappingFromRows: mappingFromRows,
  credentials: credentials,
  FIELD_OPTIONS: FIELD_OPTIONS,
  MAX_IMPORT_FILE_BYTES: MAX_IMPORT_FILE_BYTES,
  ALLOWED_IMPORT_EXTENSIONS: ALLOWED_IMPORT_EXTENSIONS,
};
